import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { CarouselItemDirective } from '@shared/components/carousel/carousel.directive';
import {
  animate,
  AnimationBuilder,
  AnimationFactory,
  AnimationPlayer,
  style,
} from '@angular/animations';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { FlexModule } from '@ngbracket/ngx-layout';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'recrewt-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'onResize()',
  },
  imports: [
    NgStyle,
    ExtendedModule,
    NgTemplateOutlet,
    FlexModule,
    MatIconButton,
    MatIcon,
    TranslateModule,
  ],
})
export class CarouselComponent {
  readonly timing = input('250ms ease-in');

  readonly showControls = input(true);

  readonly items = contentChildren(CarouselItemDirective);

  private builder = inject(AnimationBuilder);

  private player?: AnimationPlayer;

  private readonly itemWidth = signal(1);

  private readonly currentSlide = signal(0);

  readonly isFirstSlide = computed(() => this.currentSlide() === 0);

  readonly isLastSlide = computed(() => this.currentSlide() >= this.items().length - 1);

  readonly carouselWrapperStyle = computed(() => ({ width: `${this.itemWidth()}px` }));

  private readonly carousel = viewChild.required<ElementRef<HTMLUListElement>>('carousel');

  private readonly itemElements = viewChildren<ElementRef<HTMLLIElement>>('carouselItem');

  private initialized = false;

  constructor() {
    // Track item additions/removals: keep the slide index in range, re-measure, and
    // lazy-load the first item once content arrives.
    effect(() => {
      const items = this.items();
      this.itemElements();
      if (items.length === 0) return;

      this.updateWidth();
      if (this.currentSlide() > items.length - 1) {
        this.currentSlide.set(items.length - 1);
        this.step();
      }
      if (!this.initialized) {
        this.initialized = true;
        items[0].lazyLoad.emit(undefined);
      }
    });
  }

  onResize() {
    this.updateWidth();
  }

  next() {
    if (this.isLastSlide()) return;
    this.currentSlide.update((slide) => slide + 1);
    this.step();
  }

  prev() {
    if (this.isFirstSlide()) return;
    this.currentSlide.update((slide) => slide - 1);
    this.step();
  }

  private step() {
    const offset = this.currentSlide() * this.itemWidth();
    const myAnimation: AnimationFactory = this.builder.build([
      animate(this.timing(), style({ transform: `translateX(-${offset}px)` })),
    ]);

    this.player = myAnimation.create(this.carousel().nativeElement);
    this.player.play();
    this.items()[this.currentSlide()]?.lazyLoad.emit(undefined);
  }

  private updateWidth() {
    const width = this.itemElements()[0]?.nativeElement.getBoundingClientRect().width;
    if (width) {
      this.itemWidth.set(width);
    }
  }
}
